import i18n from "i18next";
import { useTranslation } from "react-i18next";

/**
 * Landing content now lives in the i18n "landing.*" namespace across all 14
 * locales (enforced by check:i18n). The data objects below hold i18n KEY
 * strings plus non-translatable structure (numbers, flags, proper nouns).
 * pick(key) resolves a key to the active-language string; useLang() subscribes
 * the component to language changes so pick() re-reads on switch.
 */
export function useLang(): string {
  const { i18n: inst } = useTranslation();
  return (inst.language || "en").split("-")[0];
}

export function pick(key: string, _lang?: string): string {
  return i18n.t(key);
}

export const HERO = {
  "tag": "landing.hero.tag",
  "h1a": "landing.hero.h1a",
  "h1b": "landing.hero.h1b",
  "sub": "landing.hero.sub",
  "ctaPrimary": "landing.hero.ctaPrimary",
  "ctaSecondary": "landing.hero.ctaSecondary",
  "microcopy": "landing.hero.microcopy"
};

export const TRUST = {
  "eyebrow": "landing.trust.eyebrow",
  "metrics": [
    {
      "value": 14,
      "suffix": "",
      "label": "landing.trust.metrics.0.label"
    },
    {
      "value": 6,
      "suffix": "",
      "label": "landing.trust.metrics.1.label"
    },
    {
      "value": 15,
      "suffix": " min",
      "label": "landing.trust.metrics.2.label"
    }
  ],
  "disclaimer": "landing.trust.disclaimer"
};

export const MOAT = {
  "eyebrow": "landing.moat.eyebrow",
  "h2": "landing.moat.h2",
  "intro": "landing.moat.intro",
  "inputs": [
    "FAX",
    "議事録",
    "稟議書",
    "手書き",
    "法人印",
    "法人番号"
  ],
  "keigo": {
    "label": "landing.moat.keigo.label",
    "tabs": [
      {
        "key": "teineigo",
        "name": "landing.moat.keigo.tabs.0.name",
        "sample": "landing.moat.keigo.tabs.0.sample"
      },
      {
        "key": "sonkeigo",
        "name": "landing.moat.keigo.tabs.1.name",
        "sample": "landing.moat.keigo.tabs.1.sample"
      },
      {
        "key": "kenjougo",
        "name": "landing.moat.keigo.tabs.2.name",
        "sample": "landing.moat.keigo.tabs.2.sample"
      },
      {
        "key": "double",
        "name": "landing.moat.keigo.tabs.3.name",
        "sample": "landing.moat.keigo.tabs.3.sample"
      }
    ]
  },
  "differentiators": [
    {
      "title": "landing.moat.differentiators.0.title",
      "body": "landing.moat.differentiators.0.body",
      "seal": true
    },
    {
      "title": "landing.moat.differentiators.1.title",
      "body": "landing.moat.differentiators.1.body"
    },
    {
      "title": "landing.moat.differentiators.2.title",
      "body": "landing.moat.differentiators.2.body"
    },
    {
      "title": "landing.moat.differentiators.3.title",
      "body": "landing.moat.differentiators.3.body"
    },
    {
      "title": "landing.moat.differentiators.4.title",
      "body": "landing.moat.differentiators.4.body"
    },
    {
      "title": "landing.moat.differentiators.5.title",
      "body": "landing.moat.differentiators.5.body"
    }
  ]
};

export const RAG = {
  "eyebrow": "landing.rag.eyebrow",
  "h2": "landing.rag.h2",
  "lanes": [
    {
      "name": "landing.rag.lanes.0.name",
      "body": "landing.rag.lanes.0.body"
    },
    {
      "name": "landing.rag.lanes.1.name",
      "body": "landing.rag.lanes.1.body"
    },
    {
      "name": "landing.rag.lanes.2.name",
      "body": "landing.rag.lanes.2.body"
    }
  ],
  "fusion": "landing.rag.fusion",
  "citationFields": [
    "landing.rag.citationFields.0",
    "landing.rag.citationFields.1",
    "landing.rag.citationFields.2",
    "landing.rag.citationFields.3"
  ],
  "tagline": "landing.rag.tagline"
};

export const AGENTS = [
  {
    "key": "search",
    "name": "landing.agents.0.name",
    "body": "landing.agents.0.body"
  },
  {
    "key": "compose",
    "name": "landing.agents.1.name",
    "body": "landing.agents.1.body"
  },
  {
    "key": "brief",
    "name": "landing.agents.2.name",
    "body": "landing.agents.2.body"
  },
  {
    "key": "compare",
    "name": "landing.agents.3.name",
    "body": "landing.agents.3.body"
  },
  {
    "key": "audit",
    "name": "landing.agents.4.name",
    "body": "landing.agents.4.body"
  },
  {
    "key": "workflow",
    "name": "landing.agents.5.name",
    "body": "landing.agents.5.body"
  }
];

export const CONNECTORS = {
  "eyebrow": "landing.connectors.eyebrow",
  "h2": "landing.connectors.h2",
  "global": [
    "Google Drive",
    "Microsoft 365",
    "Slack",
    "Notion",
    "Salesforce",
    "Confluence",
    "GitHub",
    "Jira",
    "Zendesk",
    "Box",
    "Dropbox"
  ],
  "jp": [
    "kintone",
    "Chatwork",
    "LINE WORKS"
  ],
  "globalLabel": "landing.connectors.globalLabel",
  "jpLabel": "landing.connectors.jpLabel",
  "footnote": "landing.connectors.footnote"
};

export const MATRIX = {
  "eyebrow": "landing.matrix.eyebrow",
  "h2": "landing.matrix.h2",
  "columns": [
    "Knowlez",
    "INDX",
    "Glean",
    "Onyx",
    "M365 Copilot"
  ],
  "rows": [
    {
      "cap": "landing.matrix.rows.0.cap",
      "marks": [
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "none"
        },
        {
          "v": "none"
        },
        {
          "v": "none"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.1.cap",
      "marks": [
        {
          "v": "full",
          "label": "landing.matrix.rows.1.marks.0.label"
        },
        {
          "v": "partial",
          "label": "landing.matrix.rows.1.marks.1.label"
        },
        {
          "v": "none"
        },
        {
          "v": "none"
        },
        {
          "v": "none"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.2.cap",
      "marks": [
        {
          "v": "full"
        },
        {
          "v": "partial",
          "label": "landing.matrix.rows.2.marks.1.label"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "none"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.3.cap",
      "marks": [
        {
          "v": "full",
          "label": "landing.matrix.rows.3.marks.0.label"
        },
        {
          "v": "none"
        },
        {
          "v": "partial",
          "label": "landing.matrix.rows.3.marks.2.label"
        },
        {
          "v": "partial",
          "label": "landing.matrix.rows.3.marks.3.label"
        },
        {
          "v": "partial",
          "label": "landing.matrix.rows.3.marks.4.label"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.4.cap",
      "marks": [
        {
          "v": "full"
        },
        {
          "v": "partial",
          "label": "landing.matrix.rows.4.marks.1.label"
        },
        {
          "v": "partial",
          "label": "landing.matrix.rows.4.marks.2.label"
        },
        {
          "v": "partial",
          "label": "landing.matrix.rows.4.marks.3.label"
        },
        {
          "v": "partial",
          "label": "landing.matrix.rows.4.marks.4.label"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.5.cap",
      "marks": [
        {
          "v": "full"
        },
        {
          "v": "partial",
          "label": "landing.matrix.rows.5.marks.1.label"
        },
        {
          "v": "full",
          "label": "landing.matrix.rows.5.marks.2.label"
        },
        {
          "v": "full",
          "label": "landing.matrix.rows.5.marks.3.label"
        },
        {
          "v": "partial",
          "label": "landing.matrix.rows.5.marks.4.label"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.6.cap",
      "marks": [
        {
          "v": "partial",
          "label": "landing.matrix.rows.6.marks.0.label"
        },
        {
          "v": "partial"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "none"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.7.cap",
      "marks": [
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "none",
          "label": "landing.matrix.rows.7.marks.2.label"
        },
        {
          "v": "full"
        },
        {
          "v": "none"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.8.cap",
      "marks": [
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "none"
        },
        {
          "v": "full"
        },
        {
          "v": "none"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.9.cap",
      "marks": [
        {
          "v": "full",
          "label": "landing.matrix.rows.9.marks.0.label"
        },
        {
          "v": "none"
        },
        {
          "v": "none"
        },
        {
          "v": "none",
          "label": "landing.matrix.rows.9.marks.3.label"
        },
        {
          "v": "full",
          "label": "landing.matrix.rows.9.marks.4.label"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.10.cap",
      "marks": [
        {
          "v": "full"
        },
        {
          "v": "none",
          "label": "landing.matrix.rows.10.marks.1.label"
        },
        {
          "v": "partial",
          "label": "landing.matrix.rows.10.marks.2.label"
        },
        {
          "v": "partial",
          "label": "landing.matrix.rows.10.marks.3.label"
        },
        {
          "v": "full"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.11.cap",
      "marks": [
        {
          "v": "full",
          "label": "landing.matrix.rows.11.marks.0.label"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "partial"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.12.cap",
      "marks": [
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.13.cap",
      "marks": [
        {
          "v": "full"
        },
        {
          "v": "partial",
          "label": "landing.matrix.rows.13.marks.1.label"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.14.cap",
      "marks": [
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "partial"
        },
        {
          "v": "partial"
        },
        {
          "v": "none",
          "label": "landing.matrix.rows.14.marks.4.label"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.15.cap",
      "marks": [
        {
          "v": "full",
          "label": "landing.matrix.rows.15.marks.0.label"
        },
        {
          "v": "partial"
        },
        {
          "v": "partial"
        },
        {
          "v": "partial"
        },
        {
          "v": "full"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.16.cap",
      "marks": [
        {
          "v": "full"
        },
        {
          "v": "partial",
          "label": "landing.matrix.rows.16.marks.1.label"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.17.cap",
      "marks": [
        {
          "v": "underway",
          "label": "landing.matrix.rows.17.marks.0.label"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.18.cap",
      "marks": [
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        }
      ]
    },
    {
      "cap": "landing.matrix.rows.19.cap",
      "marks": [
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "full"
        },
        {
          "v": "partial",
          "label": "landing.matrix.rows.19.marks.4.label"
        }
      ]
    }
  ]
};

export const SECURITY = {
  "eyebrow": "landing.security.eyebrow",
  "h2": "landing.security.h2",
  "groups": [
    {
      "title": "landing.security.groups.0.title",
      "items": [
        "landing.security.groups.0.items.0",
        "landing.security.groups.0.items.1"
      ]
    },
    {
      "title": "landing.security.groups.1.title",
      "items": [
        "landing.security.groups.1.items.0",
        "landing.security.groups.1.items.1"
      ]
    },
    {
      "title": "landing.security.groups.2.title",
      "items": [
        "landing.security.groups.2.items.0",
        "landing.security.groups.2.items.1"
      ]
    },
    {
      "title": "landing.security.groups.3.title",
      "items": [
        "landing.security.groups.3.items.0",
        "landing.security.groups.3.items.1"
      ]
    },
    {
      "title": "landing.security.groups.4.title",
      "items": [
        "landing.security.groups.4.items.0",
        "landing.security.groups.4.items.1"
      ]
    }
  ],
  "compliance": [
    {
      "label": "landing.security.compliance.0.label",
      "status": "underway"
    },
    {
      "label": "landing.security.compliance.1.label",
      "status": "underway"
    },
    {
      "label": "landing.security.compliance.2.label",
      "status": "shipped"
    },
    {
      "label": "landing.security.compliance.3.label",
      "status": "shipped"
    }
  ]
};

export const PRICING = {
  "eyebrow": "landing.pricing.eyebrow",
  "h2": "landing.pricing.h2",
  "plans": [
    {
      "name": "Free",
      "price": 0,
      "seats": "landing.pricing.plans.0.seats",
      "highlight": false,
      "note": "landing.pricing.plans.0.note"
    },
    {
      "name": "Growth",
      "price": 24,
      "seats": "landing.pricing.plans.1.seats",
      "highlight": true,
      "note": "landing.pricing.plans.1.note"
    },
    {
      "name": "Global",
      "price": 59,
      "seats": "landing.pricing.plans.2.seats",
      "highlight": false,
      "note": "landing.pricing.plans.2.note"
    },
    {
      "name": "Enterprise",
      "price": 129,
      "seats": "landing.pricing.plans.3.seats",
      "highlight": false,
      "note": "landing.pricing.plans.3.note"
    }
  ],
  "positioning": "landing.pricing.positioning",
  "perMonth": "landing.pricing.perMonth"
};

export const FAQ = {
  "eyebrow": "landing.faq.eyebrow",
  "h2": "landing.faq.h2",
  "subtitle": "landing.faq.subtitle",
  "support": "landing.faq.support",
  "email": "support@knowlez.com",
  "items": [
    {
      "q": "landing.faq.items.0.q",
      "a": "landing.faq.items.0.a"
    },
    {
      "q": "landing.faq.items.1.q",
      "a": "landing.faq.items.1.a"
    },
    {
      "q": "landing.faq.items.2.q",
      "a": "landing.faq.items.2.a"
    },
    {
      "q": "landing.faq.items.3.q",
      "a": "landing.faq.items.3.a"
    },
    {
      "q": "landing.faq.items.4.q",
      "a": "landing.faq.items.4.a"
    },
    {
      "q": "landing.faq.items.5.q",
      "a": "landing.faq.items.5.a"
    },
    {
      "q": "landing.faq.items.6.q",
      "a": "landing.faq.items.6.a"
    },
    {
      "q": "landing.faq.items.7.q",
      "a": "landing.faq.items.7.a"
    },
    {
      "q": "landing.faq.items.8.q",
      "a": "landing.faq.items.8.a"
    },
    {
      "q": "landing.faq.items.9.q",
      "a": "landing.faq.items.9.a"
    },
    {
      "q": "landing.faq.items.10.q",
      "a": "landing.faq.items.10.a"
    },
    {
      "q": "landing.faq.items.11.q",
      "a": "landing.faq.items.11.a"
    }
  ]
};

export const CLOSING = {
  "h2": "landing.closing.h2",
  "body": "landing.closing.body",
  "ctaPrimary": "landing.closing.ctaPrimary",
  "ctaSecondary": "landing.closing.ctaSecondary"
};

